import { ChevronDown, ChevronRight, type LucideIcon } from "lucide-react";
import { NavLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";

/**
 * Sidebar accordion primitive.
 *
 * Source: docs/engineering/contracts/UI_COMPONENT_CONTRACT.md § Canonical
 * primitive set (PROD-FE-DIRECTION-2026-05-09); spec at
 * docs/product/experience-and-ui.md § 11 Sidebar IA.
 *
 * Renders a parent nav item that expands its sub-items when the active
 * route URL prefix matches. The URL IS the accordion state — NO
 * localStorage persistence per § 11c.
 *
 * ARIA contract:
 * - Parent: aria-expanded={isOpen}; role inferred from <a> wrapping
 *   semantics (NavLink renders <a>).
 * - Sub-list: a plain `<ul>` labelled by its section (`aria-label`) and NO
 *   ARIA role — a group role on the list orphans its `<li>` children for
 *   assistive technology (axe `listitem` on every authenticated route;
 *   ENG-APP-UX-TUNEUP Track A, § 11c re-based 2026-10-15).
 * - Each sub-item: standard NavLink with focus-visible ring.
 *
 * Animation: 150ms slide-down on expand, wrapped in motion-safe: per
 * UI_COMPONENT_CONTRACT § Accessibility floor.
 */

export interface SidebarAccordionSubItem {
  label: string;
  href: string;
  icon?: LucideIcon;
}

export interface SidebarAccordionSection {
  label: string;
  icon: LucideIcon;
  /**
   * Parent route. When omitted (e.g., Learnings is a category, not a
   * route per § 11c), parent click navigates to `defaultRoute` instead.
   */
  href?: string;
  /**
   * Required when `href` is omitted. Click on the parent navigates here.
   * Also used as the navigation target when Enter is pressed on the
   * parent.
   */
  defaultRoute?: string;
  /** Keep a category expanded regardless of the current route. */
  alwaysOpen?: boolean;
  /** Render the parent without active or hover styling. */
  disableParentState?: boolean;
  /** Use the compact typography reserved for bottom utility links. */
  compact?: boolean;
}

interface SidebarAccordionProps {
  section: SidebarAccordionSection;
  subItems?: SidebarAccordionSubItem[];
  /**
   * URL-prefix matcher. Returns true when the current pathname should
   * cause the section to render expanded. Required even for sections
   * without sub-items so parent active-styling works uniformly.
   */
  isActive: (pathname: string) => boolean;
}

export default function SidebarAccordion({
  section,
  subItems,
  isActive,
}: SidebarAccordionProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const isOpen = section.alwaysOpen || isActive(location.pathname);
  const hasSubItems = !!subItems && subItems.length > 0;
  const parentTarget = section.href ?? section.defaultRoute;

  if (!parentTarget) {
    throw new Error(
      "SidebarAccordion: section requires either `href` or `defaultRoute`.",
    );
  }

  const Chevron = isOpen ? ChevronDown : ChevronRight;

  return (
    <div>
      <NavLink
        to={parentTarget}
        end={section.href === "/"}
        aria-expanded={hasSubItems ? isOpen : undefined}
        className={({ isActive: navActive }) =>
          cn(
            "flex items-center gap-3 rounded-lg px-3 py-2 font-medium transition-colors",
            section.compact ? "text-[13px]" : "text-sm",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500",
            // For category-only sections (no own href), highlight when
            // any sub-route matches per the accordion isActive predicate.
            !section.disableParentState && (section.href ? navActive : isOpen)
              ? "bg-primary-50 text-primary-text"
              : section.disableParentState
                ? "text-gray-600"
                : "text-gray-600 hover:bg-gray-50 hover:text-gray-900",
          )
        }
      >
        <section.icon size={18} className="shrink-0" />
        <span className="flex-1">{section.label}</span>
        {hasSubItems && (
          <Chevron size={16} className="shrink-0" aria-hidden="true" />
        )}
      </NavLink>
      {hasSubItems && isOpen && (
        <ul
          aria-label={t("ui.sidebarAccordion.subNavigationAriaLabel", {
            label: section.label,
          })}
          className={cn(
            "mt-1 space-y-0.5 pl-7",
            "motion-safe:animate-slide-down",
          )}
        >
          {subItems!.map((item) => (
            <li key={item.href}>
              <NavLink
                to={item.href}
                className={({ isActive: navActive }) =>
                  cn(
                    "flex items-center gap-2 rounded-md px-3 py-1.5 text-[13px] font-normal transition-colors",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500",
                    navActive
                      ? "bg-primary-50 text-primary-text"
                      : "text-gray-600 hover:bg-gray-50 hover:text-gray-900",
                  )
                }
              >
                {item.icon && (
                  <item.icon
                    size={14}
                    className="shrink-0"
                    aria-hidden="true"
                  />
                )}
                <span>{item.label}</span>
              </NavLink>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
